import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiUser, FiMail, FiCalendar, FiClock, FiArrowLeft, FiAlertCircle } from 'react-icons/fi';
import { getMenteeDetails } from '../../services/mentorService';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const MenteeDetails = () => {
  const { menteeId } = useParams();
  const [mentee, setMentee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchMenteeDetails = async () => {
      try {
        setLoading(true);
        const response = await getMenteeDetails(menteeId);
        setMentee(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching mentee details:', err);
        setError('Failed to load mentee details. Please try again later.');
        setLoading(false);
      }
    };

    fetchMenteeDetails();
  }, [menteeId]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <FiAlertCircle className="h-5 w-5 text-red-400" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!mentee) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Mentee not found.</p>
        <Link
          to="/mentor/mentees"
          className="text-primary-600 hover:text-primary-900 mt-4 inline-block"
          aria-label="Back to mentees list"
        >
          Back to Mentees
        </Link>
      </div>
    );
  }

  const safeLocaleDate = (v) => {
    if (v == null || v === '') return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
  };
  const safeLocaleString = (v) => {
    if (v == null || v === '') return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
  };

  const copy = pageDescriptions.menteeDetails;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader
        title={copy.title}
        description={copy.description}
        whatYouCanDo={copy.whatYouCanDo}
        whatYouShouldDo={copy.whatYouShouldDo}
        actions={
          <Link
            to="/mentor/mentees"
            className="inline-flex items-center text-primary-600 hover:text-primary-900"
            aria-label="Back to mentees list"
          >
            <FiArrowLeft className="mr-2" /> Back to Mentees
          </Link>
        }
      />

      <div className="card overflow-hidden mb-8">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h2 className="text-lg leading-6 font-medium text-gray-900">Mentee Profile</h2>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Personal details and information.</p>
          </div>
          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full 
            ${mentee.membership_type === 'gold' ? 'bg-yellow-100 text-yellow-800' : 
              mentee.membership_type === 'platinum' ? 'bg-green-100 text-green-800' : 
              'bg-gray-100 text-gray-800'}`}
          >
            {mentee.membership_type || '—'}
          </span>
        </div>
        <div className="border-t border-gray-200">
          <dl>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiUser className="mr-2" /> Full name
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{mentee.full_name || '—'}</dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiMail className="mr-2" /> Email address
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">{mentee.email || '—'}</dd>
            </div>
            <div className="bg-gray-50 px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiCalendar className="mr-2" /> Joined
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {safeLocaleDate(mentee.created_at)}
              </dd>
            </div>
            <div className="bg-white px-4 py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <FiClock className="mr-2" /> Last active
              </dt>
              <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                {mentee.last_login ? safeLocaleString(mentee.last_login) : 'Never'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Applications Summary */}
      <div className="card overflow-hidden mb-8">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Application Summary</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Overview of job applications.</p>
        </div>
        <div className="border-t border-gray-200">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-primary-600">{mentee.application_stats?.total || 0}</div>
                <div className="text-sm text-gray-500">Total Applications</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-600">{mentee.application_stats?.applied || 0}</div>
                <div className="text-sm text-gray-500">Applied</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-600">{mentee.application_stats?.interview || 0}</div>
                <div className="text-sm text-gray-500">Interviews</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-600">{mentee.application_stats?.rejected || 0}</div>
                <div className="text-sm text-gray-500">Rejected</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card overflow-hidden">
        <div className="px-4 py-5 sm:px-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Recent Activity</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Latest actions and updates.</p>
        </div>
        <div className="border-t border-gray-200">
          {mentee.recent_activity && mentee.recent_activity.length > 0 ? (
            <ul className="divide-y divide-gray-200">
              {mentee.recent_activity.map((activity, index) => (
                <li key={index} className="px-4 py-4 sm:px-6">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-primary-600">{activity.type}</div>
                    <div className="text-sm text-gray-500">{safeLocaleString(activity.timestamp)}</div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">{activity.description}</div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-4 py-5 sm:px-6 text-center text-gray-500">
              No recent activity found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenteeDetails;